/**
 * High5 — Gerçekleşen Net Satış (ERP) Excel yükleme sayfası.
 *
 * ERP'nin "High5 Haftalık Net Marka Raporu" export'unu yükler; veri
 * erp_weekly_net_sales_by_brand tablosuna yazılır ve /ads-platforms/unified-campaigns
 * sayfasındaki "Marka Bazlı Haftalık Harcama · Gerçekleşen Net Satış · ROAS" kartını besler.
 *
 * Sadece High5. Başka marka seçiliyken sayfa uyarı gösterir (menüde de görünmez).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/Card';
import {
  Upload, RefreshCw, CheckCircle2, AlertTriangle, FileSpreadsheet, Trash2, Info,
} from 'lucide-react';
import { HIGH5_BRAND_ID } from '../../config/brandGroups';

const API_BASE = 'https://api.glowytics.com';

const fmtTL = (v) => `₺${Math.round(Number(v) || 0).toLocaleString('tr-TR')}`;
const fmtTLFull = (v) => `₺${(Number(v) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return String(d); }
};
const weekRangeLabel = (weekStart) => {
  if (!weekStart) return '—';
  const s = new Date(weekStart + 'T00:00:00');
  const e = new Date(s); e.setDate(e.getDate() + 6);
  const f = (d) => d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  return `${f(s)} – ${f(e)}`;
};

export default function NetSalesUpload() {
  const { selectedBrand } = useAuth();
  const isHigh5 = selectedBrand?.id === HIGH5_BRAND_ID;

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [isoYear, setIsoYear] = useState(new Date().getFullYear());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { report, selection, written, deleted, stored }
  const [preview, setPreview] = useState(null); // { report, selection } — dry-run
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [pickedWeeks, setPickedWeeks] = useState([]); // silme için işaretlenenler: "2026-31"
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const fetchSummary = useCallback(async () => {
    if (!isHigh5) { setSummary(null); return; }
    setSummaryLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/high5-net-sales/summary`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) { setSummary(null); return; }
      setSummary(await r.json());
    } catch (_) {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [isHigh5, selectedBrand?.id]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Aynı gövde hem önizleme (dryRun) hem gerçek yükleme için — önizlemede
  // görülen rakamların yazılanla aynı kod yolundan geldiğinden emin olmak için.
  // weeksOverride: dosya ilk okunduğunda seçim henüz state'e yazılmamış olur.
  const send = async ({ dryRun, weeksOverride, fileOverride }) => {
    const f = fileOverride || file;
    if (!f) { setError('Önce bir .xlsx dosyası seçin.'); return; }
    setBusy(true); setError(null);
    if (!dryRun) setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('isoYear', String(isoYear));
      const weeks = weeksOverride !== undefined ? weeksOverride : selectedWeeks;
      if (weeks && weeks.length) fd.append('weeks', weeks.join(','));
      if (dryRun) fd.append('dryRun', '1');
      const r = await fetch(`${API_BASE}/api/high5-net-sales/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
        body: fd,
      });
      const json = await r.json();
      if (!r.ok) {
        setError(json?.error || 'Yükleme başarısız.');
        if (json?.report) setPreview({ report: json.report, selection: json.selection });
        return;
      }
      if (dryRun) {
        setPreview({ report: json.report, selection: json.selection });
        setResult(null);
        // Varsayılan seçim: SADECE tabloda olmayan haftalar. Örtüşen haftalar
        // kullanıcı bilerek işaretlemedikçe üzerine yazılmaz.
        if (weeksOverride === null) {
          const fresh = json.report?.newWeeks || [];
          setSelectedWeeks(fresh.length ? fresh : (json.report?.weeks || []));
        }
      } else {
        setResult(json);
        setPreview(null);
        await fetchSummary();
      }
    } catch (e) {
      setError(e.message || 'Beklenmeyen hata.');
    } finally {
      setBusy(false);
    }
  };

  const deleteWeek = async (y, w) => {
    const row = (summary?.weeks || []).find(x => x.iso_year === y && x.iso_week === w);
    const detay = row ? ` (${fmtTL(row.amount)} · ${row.brand_rows} marka)` : '';
    if (!window.confirm(`${y} yılı ${w}. hafta verisi silinsin mi?${detay}\n\nBu işlem geri alınamaz.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/high5-net-sales/week/${y}/${w}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setError(j?.error || 'Silme başarısız.');
        return;
      }
      setPickedWeeks(prev => prev.filter(k => k !== `${y}-${w}`));
      await fetchSummary();
    } finally {
      setBusy(false);
    }
  };

  // Toplu silme — "Kayıtlı veri" listesinden işaretlenen haftalar.
  const deletePickedWeeks = async () => {
    if (!pickedWeeks.length) return;
    const rows = (summary?.weeks || []).filter(w => pickedWeeks.includes(`${w.iso_year}-${w.iso_week}`));
    const toplam = rows.reduce((s, w) => s + Number(w.amount || 0), 0);
    const liste = rows.map(w => `${w.iso_year} H${w.iso_week}`).join(', ');
    if (!window.confirm(
      `${rows.length} hafta silinsin mi?\n\n${liste}\n\nToplam ${fmtTL(toplam)} veri silinecek. Bu işlem geri alınamaz.`
    )) return;

    setBusy(true); setError(null);
    try {
      // Yıl bazında grupla — endpoint tek yıl + hafta listesi alıyor.
      const byYear = new Map();
      for (const w of rows) {
        if (!byYear.has(w.iso_year)) byYear.set(w.iso_year, []);
        byYear.get(w.iso_year).push(w.iso_week);
      }
      let deleted = 0;
      for (const [year, weeks] of byYear) {
        const r = await fetch(`${API_BASE}/api/high5-net-sales/weeks`, {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
          body: JSON.stringify({ isoYear: year, weeks }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) { setError(j?.error || 'Silme başarısız.'); return; }
        deleted += Number(j.deleted || 0);
      }
      setPickedWeeks([]);
      await fetchSummary();
      setError(null);
      setDeleteInfo(`${deleted} satır silindi (${liste}).`);
    } finally {
      setBusy(false);
    }
  };

  const togglePicked = (key) => setPickedWeeks(prev =>
    prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
  );

  // Dosya seçilir seçilmez otomatik ön kontrol: hafta listesi ve hangilerinin
  // tabloda zaten olduğu görünmeden "Yükle" butonuna basılmasın.
  const pickFile = (f) => {
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) { setError('Sadece .xlsx dosyası yüklenebilir.'); return; }
    setError(null); setPreview(null); setResult(null); setSelectedWeeks([]); setFile(f);
    send({ dryRun: true, weeksOverride: null, fileOverride: f });
  };

  const toggleWeek = (w) => setSelectedWeeks(prev =>
    prev.includes(w) ? prev.filter(x => x !== w) : [...prev, w].sort((a, b) => a - b)
  );

  const yearOpts = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 1, y, y + 1];
  }, []);

  const report = result?.report || preview?.report;
  const selection = result?.selection || preview?.selection;
  const overwriteCount = report ? selectedWeeks.filter(w => (report.existingWeeks || []).includes(w)).length : 0;

  if (!isHigh5) {
    return (
      <div className="p-6">
        <Card className="rounded-2xl p-8 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <h2 className="font-semibold text-gray-800">Bu sayfa yalnızca High5 için</h2>
          <p className="text-sm text-gray-500 mt-1">
            Gerçekleşen net satış verisi şu an sadece High5 markasında tutuluyor. Üstteki marka seçiciden High5'i seçin.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Gerçekleşen Net Satış — Excel Yükleme</h1>
        <p className="text-sm text-gray-500 mt-1">
          ERP'den alınan "High5 2026 YTD Hafta-Marka Bazlı Net Satış" dosyasını (ya da eski "Haftalık Net Marka Raporu"nu) buraya yükleyin. Veri,
          Birleşik Kampanyalar sayfasındaki <strong>Gerçekleşen Net Satış · ROAS</strong> tablosunu besler.
        </p>
      </div>

      {/* Yükleme kutusu */}
      <Card className="rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl border border-indigo-200 bg-indigo-50 flex items-center justify-center">
            <Upload className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-800">Dosya yükle</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              .xlsx · Kolonlar: Tarih Hafta ID · Brand / New Product Brand DESC · Perakende Satış Net Tutar (VH) · Perakende Satış Net Adet · Lokasyon: WEB HIGH5
            </p>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl px-6 py-10 text-center cursor-pointer transition-colors ${
              dragOver ? 'border-indigo-400 bg-indigo-50/50' : 'border-gray-200 hover:border-gray-300 bg-gray-50/40'
            }`}
          >
            <FileSpreadsheet className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            {file ? (
              <>
                <div className="font-medium text-gray-800">{file.name}</div>
                <div className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(0)} KB · değiştirmek için tıklayın</div>
              </>
            ) : (
              <>
                <div className="font-medium text-gray-700">Dosyayı buraya sürükleyin veya tıklayıp seçin</div>
                <div className="text-xs text-gray-500 mt-1">Sadece .xlsx</div>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
          </div>

          {/* Yıl seçimi — dosyada yıl kolonu YOK, hafta numarası tek başına yıl taşımıyor. */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700">Hafta numaralarının yılı:</label>
              <select
                value={isoYear}
                onChange={(e) => setIsoYear(Number(e.target.value))}
                className="px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white"
              >
                {yearOpts.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Info className="w-3.5 h-3.5" />
              Dosyada yıl bilgisi yok; hafta 1–53 ISO haftası olarak bu yıla göre tarihe çevrilir.
            </div>
            <div className="flex-1" />
            <button
              onClick={() => send({ dryRun: true })}
              disabled={busy || !file}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
            >
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Yeniden kontrol et
            </button>
            <button
              onClick={() => send({ dryRun: false })}
              disabled={busy || !file || !selectedWeeks.length}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-gray-900 text-white hover:bg-gray-800 shadow-sm disabled:opacity-50"
            >
              {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {selectedWeeks.length
                ? `Seçili ${selectedWeeks.length} haftayı yükle`
                : 'Yüklenecek hafta seçin'}
            </button>
          </div>

          {/* Hafta seçici — dosya 16-32. haftaları içerip sadece 32 istendiğinde
              olan yer burası. Dosya seçilince otomatik dolar; varsayılan olarak
              SADECE tabloda olmayan haftalar işaretli gelir. */}
          {report?.weeks?.length > 0 && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-gray-800">
                  Hangi haftalar yüklensin?
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    dosyada {report.weeks.length} hafta var · {report.newWeeks?.length || 0} tanesi tabloda yok
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setSelectedWeeks(report.newWeeks || [])}
                    disabled={!report.newWeeks?.length}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40"
                  >
                    Sadece yeni haftalar
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedWeeks(report.weeks)}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                  >
                    Tümü
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedWeeks([])}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                  >
                    Temizle
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {report.weeks.map(w => {
                  const exists = (report.existingWeeks || []).includes(w);
                  const sel = selectedWeeks.includes(w);
                  return (
                    <button
                      key={w}
                      type="button"
                      onClick={() => toggleWeek(w)}
                      title={exists ? 'Tabloda zaten var — seçilirse ÜZERİNE YAZILIR' : 'Tabloda yok — yeni eklenecek'}
                      className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                        sel
                          ? (exists
                              ? 'bg-amber-500 text-white border-amber-500'
                              : 'bg-emerald-600 text-white border-emerald-600')
                          : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      H{w}{exists ? ' •' : ''}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded bg-emerald-600" /> yeni hafta
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded bg-amber-500" /> tabloda var (•) — seçiliyse üzerine yazılır
                </span>
              </div>

              {overwriteCount > 0 && (
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    Seçili haftaların <strong>{overwriteCount} tanesi tabloda zaten var</strong> —
                    o haftaların mevcut satırları silinip dosyadaki hâliyle yeniden yazılacak.
                    Sadece yenileri istiyorsan "Sadece yeni haftalar"a bas.
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
            <strong>Yükleme davranışı:</strong> sadece <strong>seçtiğin haftalar</strong> yazılır. Seçilen bir hafta
            tabloda varsa o haftanın satırları silinip dosyadaki hâliyle yeniden yazılır (mükerrer kayıt oluşmaz).
            Seçmediğin haftalara hiç dokunulmaz.
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>{error}</div>
            </div>
          )}
        </div>
      </Card>

      {/* Mutabakat raporu */}
      {report && (
        <Card className="rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            {result ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Info className="w-5 h-5 text-gray-500" />}
            <h2 className="font-semibold text-gray-800">
              {result ? 'Yükleme tamamlandı' : 'Ön kontrol (kayıt yapılmadı)'}
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="text-xs text-gray-500">Dosyanın tamamı:</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Okunan satır', value: report.parsedRows },
                { label: 'Hafta', value: `${report.weekCount} (${report.weeks?.[0]}–${report.weeks?.[report.weeks.length - 1]})` },
                { label: 'Toplam net satış', value: fmtTLFull(report.sumAmount) },
                { label: 'Toplam net adet', value: (report.sumQty || 0).toLocaleString('tr-TR') },
              ].map(s => (
                <div key={s.label} className="rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3">
                  <div className="text-xs text-gray-500">{s.label}</div>
                  <div className="text-lg font-semibold text-gray-900 tabular-nums">{s.value}</div>
                </div>
              ))}
            </div>

            {/* Seçim özeti — dosyanın tamamı ile gerçekte yazılan/yazılacak olan
                arasındaki farkı açıkça göster. */}
            {selection && selection.weeks?.length !== report.weeks?.length && (
              <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-900">
                <strong>{result ? 'Yazılan' : 'Yazılacak'} seçim:</strong>{' '}
                {selection.weeks.length} hafta (H{selection.weeks.join(', H')}) ·{' '}
                {selection.rows} satır · {fmtTLFull(selection.sumAmount)} · {selection.sumQty} adet
                {selection.skippedWeeks?.length > 0 && (
                  <div className="text-xs text-indigo-700/80 mt-1">
                    Dokunulmayan haftalar: H{selection.skippedWeeks.join(', H')}
                  </div>
                )}
              </div>
            )}

            {/* Dosyanın kendi Total satırıyla checksum — satır sayısından çok daha güvenilir. */}
            {report.fileTotalAmount != null && (
              <div className={`flex items-start gap-2 text-sm rounded-xl px-4 py-3 border ${
                report.amountMatches && report.qtyMatches
                  ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
                  : 'text-red-700 bg-red-50 border-red-100'
              }`}>
                {report.amountMatches && report.qtyMatches
                  ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                  : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
                <div>
                  Dosyanın <strong>Total</strong> satırı: {fmtTLFull(report.fileTotalAmount)} / {report.fileTotalQty} adet —{' '}
                  {report.amountMatches && report.qtyMatches
                    ? 'okunan toplamla birebir tutuyor.'
                    : 'okunan toplamla TUTMUYOR. Dosya formatı değişmiş olabilir, yüklemeden önce kontrol edin.'}
                </div>
              </div>
            )}

            {report.missingWeeks?.length > 0 && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                Aralıkta eksik hafta var: {report.missingWeeks.join(', ')}
              </div>
            )}

            {report.negativeRows > 0 && (
              <div className="text-xs text-gray-500">
                {report.negativeRows} satır negatif (iade/iptal netleştirmesi) — bunlar olduğu gibi korunur.
              </div>
            )}

            {report.unmatchedBrands?.length > 0 && (
              <div className="text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                <strong>Marka listesinde olmayan {report.unmatchedBrands.length} marka</strong> —
                tabloda "Diğer (eşleşmeyen)" satırında toplanır:{' '}
                {report.unmatchedBrands.join(', ')}
              </div>
            )}

            {report.skipped?.length > 0 && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                <strong>{report.skipped.length} satır atlandı:</strong>
                <ul className="mt-1 list-disc list-inside">
                  {report.skipped.slice(0, 10).map((s, i) => <li key={i}>Satır {s.row}: {s.reason}</li>)}
                </ul>
                {report.skipped.length > 10 && <div className="mt-1">… ve {report.skipped.length - 10} tane daha</div>}
              </div>
            )}

            {result && (
              <div className="text-sm text-gray-700">
                {result.deleted} eski satır silindi, <strong>{result.written} satır yazıldı</strong>.
                Tabloda yazılan haftalar için şu an {result.stored?.rows} satır / {fmtTLFull(result.stored?.amount)} duruyor.
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Kayıtlı veri özeti */}
      <Card className="rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-gray-800">Kayıtlı veri</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {summary?.totals
                ? `${summary.totals.weeks} hafta · ${summary.totals.brands} marka · ${summary.totals.rows} satır · ${fmtTLFull(summary.totals.amount)} · ${summary.totals.qty} adet`
                : 'Henüz veri yok'}
            </p>
          </div>
          <button
            onClick={fetchSummary}
            disabled={summaryLoading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 shadow-sm disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${summaryLoading ? 'animate-spin' : ''}`} />
            Yenile
          </button>
        </div>

        {deleteInfo && (
          <div className="px-6 py-3 border-b border-gray-100 bg-emerald-50/70 text-sm text-emerald-800 flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> {deleteInfo}
            </span>
            <button onClick={() => setDeleteInfo(null)} className="text-xs text-emerald-700/70 hover:text-emerald-900">kapat</button>
          </div>
        )}

        {/* Toplu silme çubuğu — sadece seçim varken görünür. */}
        {pickedWeeks.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-100 bg-red-50/70 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-red-800">
              <strong>{pickedWeeks.length} hafta seçildi</strong>
              {' · '}
              {fmtTL((summary?.weeks || [])
                .filter(w => pickedWeeks.includes(`${w.iso_year}-${w.iso_week}`))
                .reduce((s, w) => s + Number(w.amount || 0), 0))}
              {' veri silinecek'}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPickedWeeks([])}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
              >
                Seçimi temizle
              </button>
              <button
                onClick={deletePickedWeeks}
                disabled={busy}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Seçili haftaları sil
              </button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-0 md:divide-x divide-gray-100">
          <div className="overflow-y-auto max-h-[420px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-2.5 text-left w-8">
                    <input
                      type="checkbox"
                      title="Tümünü seç"
                      className="rounded border-gray-300"
                      checked={(summary?.weeks || []).length > 0 && pickedWeeks.length === (summary?.weeks || []).length}
                      onChange={(e) => setPickedWeeks(
                        e.target.checked ? (summary?.weeks || []).map(w => `${w.iso_year}-${w.iso_week}`) : []
                      )}
                    />
                  </th>
                  <th className="px-4 py-2.5 text-left">Hafta</th>
                  <th className="px-4 py-2.5 text-left">Tarih</th>
                  <th className="px-4 py-2.5 text-right">Net Satış</th>
                  <th className="px-4 py-2.5 text-right">Adet</th>
                  <th className="px-4 py-2.5 text-right">Marka</th>
                  <th className="px-4 py-2.5 text-right w-10"></th>
                </tr>
              </thead>
              <tbody>
                {(summary?.weeks || []).length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-gray-400">Kayıt yok</td></tr>
                )}
                {(summary?.weeks || []).map(w => {
                  const key = `${w.iso_year}-${w.iso_week}`;
                  const picked = pickedWeeks.includes(key);
                  return (
                  <tr key={key} className={`border-t border-gray-100 ${picked ? 'bg-red-50/60' : 'hover:bg-gray-50/50'}`}>
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                        checked={picked}
                        onChange={() => togglePicked(key)}
                      />
                    </td>
                    <td className="px-4 py-2 text-gray-800 font-medium">{w.iso_year} H{w.iso_week}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{weekRangeLabel(w.week_start_date)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-800">{fmtTL(w.amount)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">{w.qty}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">{w.brand_rows}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => deleteWeek(w.iso_year, w.iso_week)}
                        disabled={busy}
                        title="Bu haftayı sil"
                        className="p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="overflow-y-auto max-h-[420px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-2.5 text-left">Marka (dosyadaki)</th>
                  <th className="px-4 py-2.5 text-left">Eşleşen</th>
                  <th className="px-4 py-2.5 text-right">Net Satış</th>
                  <th className="px-4 py-2.5 text-right">Hafta</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.brands || []).length === 0 && (
                  <tr><td colSpan={4} className="py-8 text-center text-gray-400">Kayıt yok</td></tr>
                )}
                {(summary?.brands || []).map(b => (
                  <tr key={b.brand_raw} className="border-t border-gray-100 hover:bg-gray-50/50">
                    <td className="px-4 py-2 text-gray-800">{b.brand_raw}</td>
                    <td className="px-4 py-2">
                      {b.brand_key === 'Other' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500 border border-gray-200">
                          Diğer
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {b.brand_key}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-800">{fmtTL(b.amount)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">{b.weeks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {(summary?.imports || []).length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/60 text-xs text-gray-500">
            Son yüklemeler:{' '}
            {summary.imports.map((i, idx) => (
              <span key={idx}>
                {idx > 0 && ' · '}
                {i.source_file || '(dosya adı yok)'} — {fmtDate(i.imported_at)}{i.imported_by ? ` · ${i.imported_by}` : ''}
              </span>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
